import type { ReactNode } from "react";
import { CaretDownIcon, CheckIcon, MagnifyingGlassIcon, MinusIcon, WarningCircleIcon, XIcon } from "@phosphor-icons/react";
import {
  Button as AriaButton,
  Checkbox as AriaCheckbox,
  ComboBox,
  FieldError,
  Group,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  NumberField as AriaNumberField,
  Popover,
  Radio,
  RadioGroup,
  SearchField as AriaSearchField,
  Select,
  SelectionIndicator,
  SelectValue,
  Switch as AriaSwitch,
  Tag,
  TagGroup,
  TagList,
  Text,
  TextArea,
  TextField as AriaTextField,
  ToggleButton,
  ToggleButtonGroup,
  type CheckboxProps,
  type Key,
  type NumberFieldProps,
  type SwitchProps,
  type TextFieldProps as AriaTextFieldProps,
  type ValidationResult,
} from "react-aria-components";
import { cx } from "../cx.ts";

export interface FieldBase {
  label?: ReactNode;
  description?: ReactNode;
  errorMessage?: string | ((v: ValidationResult) => string);
  /** Tekst achter het label voor optionele velden, bv. "(optioneel)". Komt uit de app-catalogus. */
  optionalText?: string;
  className?: string;
}

export function FieldLabel({ label, optionalText }: Pick<FieldBase, "label" | "optionalText">) {
  if (!label) return null;
  return (
    <Label className="ui-label">
      {label}
      {optionalText ? <span className="ui-label__optional"> {optionalText}</span> : null}
    </Label>
  );
}

export function Feedback({ description, errorMessage }: Pick<FieldBase, "description" | "errorMessage">) {
  return (
    <>
      {description ? (
        <Text slot="description" className="ui-description">
          {description}
        </Text>
      ) : null}
      <FieldError className="ui-error">
        {(v) => (
          <>
            <WarningCircleIcon aria-hidden weight="bold" />
            {typeof errorMessage === "function" ? errorMessage(v) : (errorMessage ?? v.validationErrors.join(" "))}
          </>
        )}
      </FieldError>
    </>
  );
}

// ---------- tekst ----------
export interface TextFieldProps extends Omit<AriaTextFieldProps, "className">, FieldBase {
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  prefix?: ReactNode;
  suffix?: ReactNode;
  inputClassName?: string;
}

export function TextField({ label, description, errorMessage, optionalText, className, placeholder, multiline, rows, prefix, suffix, inputClassName, ...rest }: TextFieldProps) {
  return (
    <AriaTextField {...rest} className={cx("ui-field", className)}>
      <FieldLabel label={label} optionalText={optionalText} />
      {multiline ? (
        <TextArea className={cx("ui-textarea", inputClassName)} placeholder={placeholder} rows={rows} />
      ) : prefix || suffix ? (
        <Group className="ui-input-group">
          {prefix ? <span className="ui-input-group__affix">{prefix}</span> : null}
          <Input className={cx("ui-input", inputClassName)} placeholder={placeholder} />
          {suffix ? <span className="ui-input-group__affix">{suffix}</span> : null}
        </Group>
      ) : (
        <Input className={cx("ui-input", inputClassName)} placeholder={placeholder} />
      )}
      <Feedback description={description} errorMessage={errorMessage} />
    </AriaTextField>
  );
}

// ---------- getal ----------
export interface NumberFieldInputProps extends Omit<NumberFieldProps, "className">, FieldBase {
  placeholder?: string;
  suffix?: ReactNode;
}

export function NumberInput({ label, description, errorMessage, optionalText, className, placeholder, suffix, ...rest }: NumberFieldInputProps) {
  return (
    <AriaNumberField {...rest} className={cx("ui-field", className)}>
      <FieldLabel label={label} optionalText={optionalText} />
      {suffix ? (
        <Group className="ui-input-group">
          <Input className="ui-input num" placeholder={placeholder} />
          <span className="ui-input-group__affix">{suffix}</span>
        </Group>
      ) : (
        <Input className="ui-input num" placeholder={placeholder} />
      )}
      <Feedback description={description} errorMessage={errorMessage} />
    </AriaNumberField>
  );
}

// ---------- zoeken ----------
export interface SearchInputProps {
  label: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  placeholder?: string;
  clearLabel: string;
  className?: string;
  autoFocus?: boolean;
}

export function SearchInput({ label, clearLabel, placeholder, className, ...rest }: SearchInputProps) {
  return (
    <AriaSearchField {...rest} aria-label={label} className={cx("ui-search", className)}>
      <Group className="ui-input-group">
        <MagnifyingGlassIcon aria-hidden />
        <Input className="ui-input" placeholder={placeholder} />
        <AriaButton className="ui-button ui-button--ghost ui-button--sm ui-button--icon ui-search__clear" aria-label={clearLabel}>
          <XIcon aria-hidden />
        </AriaButton>
      </Group>
    </AriaSearchField>
  );
}

// ---------- keuzelijst ----------
export interface Option {
  id: Key;
  label: string;
  description?: string;
  isDisabled?: boolean;
}

export interface SelectFieldProps extends FieldBase {
  options: Option[];
  selectedKey?: Key | null;
  defaultSelectedKey?: Key;
  onSelectionChange?: (key: Key | null) => void;
  placeholder?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  name?: string;
  "aria-label"?: string;
}

export function SelectField({ label, description, errorMessage, optionalText, className, options, placeholder, ...rest }: SelectFieldProps) {
  return (
    <Select {...rest} placeholder={placeholder} className={cx("ui-field", className)}>
      <FieldLabel label={label} optionalText={optionalText} />
      <AriaButton className="ui-select-trigger">
        <SelectValue className="ui-select-value truncate" />
        <CaretDownIcon aria-hidden />
      </AriaButton>
      <Feedback description={description} errorMessage={errorMessage} />
      <Popover className="ui-popover">
        <ListBox className="ui-listbox" items={options}>
          {(o) => (
            <ListBoxItem id={o.id} textValue={o.label} isDisabled={o.isDisabled} className="ui-option">
              {o.label}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </Select>
  );
}

export interface ComboFieldProps extends FieldBase {
  options: Option[];
  selectedKey?: Key | null;
  onSelectionChange?: (key: Key | null) => void;
  placeholder?: string;
  isRequired?: boolean;
  allowsCustomValue?: boolean;
  inputValue?: string;
  onInputChange?: (v: string) => void;
}

export function ComboField({ label, description, errorMessage, optionalText, className, options, placeholder, ...rest }: ComboFieldProps) {
  return (
    <ComboBox {...rest} defaultItems={options} className={cx("ui-field", className)} menuTrigger="focus">
      <FieldLabel label={label} optionalText={optionalText} />
      <Group className="ui-input-group">
        <Input className="ui-input" placeholder={placeholder} />
        <AriaButton className="ui-button ui-button--ghost ui-button--sm ui-button--icon" excludeFromTabOrder>
          <CaretDownIcon aria-hidden />
        </AriaButton>
      </Group>
      <Feedback description={description} errorMessage={errorMessage} />
      <Popover className="ui-popover">
        <ListBox className="ui-listbox">
          {(o: Option) => (
            <ListBoxItem id={o.id} textValue={o.label} className="ui-option">
              <span>
                {o.label}
                {o.description ? <span className="subtle"> {o.description}</span> : null}
              </span>
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </ComboBox>
  );
}

// ---------- vinkjes en schakelaars ----------
export function Checkbox({ children, className, ...rest }: Omit<CheckboxProps, "className" | "children"> & { children?: ReactNode; className?: string }) {
  return (
    <AriaCheckbox {...rest} className={cx("ui-checkbox", className)}>
      {({ isIndeterminate }) => (
        <>
          <span className="ui-checkbox__box" aria-hidden>
            {isIndeterminate ? <MinusIcon weight="bold" /> : <CheckIcon weight="bold" />}
          </span>
          {children}
        </>
      )}
    </AriaCheckbox>
  );
}

export function Switch({ children, className, ...rest }: Omit<SwitchProps, "className" | "children"> & { children?: ReactNode; className?: string }) {
  return (
    <AriaSwitch {...rest} className={cx("ui-switch", className)}>
      <span className="ui-switch__track" aria-hidden>
        <span className="ui-switch__thumb" />
      </span>
      {children}
    </AriaSwitch>
  );
}

export interface RadioFieldProps extends FieldBase {
  options: Option[];
  value?: string | null;
  defaultValue?: string;
  onChange?: (value: string) => void;
  isRequired?: boolean;
  orientation?: "horizontal" | "vertical";
}

export function RadioField({ label, description, errorMessage, optionalText, className, options, ...rest }: RadioFieldProps) {
  return (
    <RadioGroup {...rest} className={cx("ui-field", className)}>
      <FieldLabel label={label} optionalText={optionalText} />
      <div className="ui-radio-group">
        {options.map((o) => (
          <Radio key={String(o.id)} value={String(o.id)} isDisabled={o.isDisabled} className="ui-radio">
            <span className="ui-radio__dot" aria-hidden />
            <span>
              {o.label}
              {o.description ? <span className="ui-description"> {o.description}</span> : null}
            </span>
          </Radio>
        ))}
      </div>
      <Feedback description={description} errorMessage={errorMessage} />
    </RadioGroup>
  );
}

/** Gesegmenteerde keuze (één waarde) met een indicator die meeschuift. */
export function Segmented({ options, value, onChange, label, className }: { options: { id: string; label: ReactNode; icon?: ReactNode }[]; value: string; onChange: (id: string) => void; label: string; className?: string }) {
  return (
    <ToggleButtonGroup
      aria-label={label}
      className={cx("ui-segmented", className)}
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[value]}
      onSelectionChange={(keys) => {
        const next = [...keys][0];
        if (next != null) onChange(String(next));
      }}
    >
      {options.map((o) => (
        <ToggleButton key={o.id} id={o.id} className="ui-segmented__item">
          <SelectionIndicator className="ui-segmented__indicator" />
          {o.icon}
          <span>{o.label}</span>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

/** Meerkeuze als chips (kenmerken, uitzicht, talen). */
export function ChipGroup({ label, options, selected, onChange, description, className }: { label: string; options: { id: string; label: string }[]; selected: string[]; onChange: (ids: string[]) => void; description?: ReactNode; className?: string }) {
  return (
    <TagGroup
      className={cx("ui-field", className)}
      selectionMode="multiple"
      selectedKeys={new Set(selected)}
      onSelectionChange={(keys) => onChange(keys === "all" ? options.map((o) => o.id) : [...keys].map(String))}
    >
      <Label className="ui-label">{label}</Label>
      <TagList className="ui-taglist" items={options}>
        {(o) => (
          <Tag id={o.id} textValue={o.label} className="ui-tag ui-tag--selectable">
            {({ isSelected }) => (
              <>
                {isSelected ? <CheckIcon aria-hidden weight="bold" /> : null}
                {o.label}
              </>
            )}
          </Tag>
        )}
      </TagList>
      {description ? (
        <Text slot="description" className="ui-description">
          {description}
        </Text>
      ) : null}
    </TagGroup>
  );
}

/** Compacte filterpil voor boven lijsten: label en waarde in één knop, oplichtend zodra er gefilterd wordt. */
export function FilterSelect({ label, options, selectedKey, onSelectionChange, defaultKey = "", className }: { label: string; options: Option[]; selectedKey: Key; onSelectionChange: (key: Key | null) => void; defaultKey?: Key; className?: string }) {
  const active = String(selectedKey) !== String(defaultKey);
  return (
    <Select aria-label={label} selectedKey={selectedKey} onSelectionChange={onSelectionChange} className={cx("ui-filter", active && "ui-filter--active", className)}>
      <AriaButton className="ui-filter__trigger">
        <span className="ui-filter__label">{label}</span>
        <SelectValue className="ui-filter__value" />
        <CaretDownIcon aria-hidden />
      </AriaButton>
      <Popover className="ui-popover" placement="bottom start">
        <ListBox className="ui-listbox" items={options}>
          {(o) => (
            <ListBoxItem id={o.id} textValue={o.label} isDisabled={o.isDisabled} className="ui-option">
              {o.label}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </Select>
  );
}
